import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  Info,
  Layers,
  Loader2,
  PackagePlus,
  Search,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  useCreateMovement,
  useWarehouses,
} from "@/lib/api/inventory.queries";
import { useProducts } from "@/lib/api/products.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { ManualMovementKind } from "@/lib/api/inventory";


export const Route = createFileRoute(
  "/companies/$companyId/inventario/movimientos/new",
)({
  component: NewMovementPage,
});

type KindMeta = {
  label: string;
  description: string;
  direction: "in" | "out";
  icon: typeof PackagePlus;
};

const KIND_META: Record<ManualMovementKind, KindMeta> = {
  initial_load: {
    label: "Carga inicial",
    description: "Cantidad inicial al activar el control de inventario.",
    direction: "in",
    icon: Layers,
  },
  purchase: {
    label: "Compra manual",
    description:
      "Compra que no tiene un documento soporte electrónico vinculado.",
    direction: "in",
    icon: PackagePlus,
  },
  adjustment_in: {
    label: "Ajuste positivo",
    description: "Sobrante encontrado en bodega o reclasificación de saldo.",
    direction: "in",
    icon: TrendingUp,
  },
  adjustment_out: {
    label: "Ajuste negativo",
    description: "Faltante encontrado o reclasificación de saldo.",
    direction: "out",
    icon: TrendingDown,
  },
  write_off: {
    label: "Baja / merma",
    description: "Producto dañado, vencido o pérdida no recuperable.",
    direction: "out",
    icon: ArrowDown,
  },
};

function NewMovementPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewMovementContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewMovementContent() {
  const fmtCop = useMoneyFormatter();
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const productsQuery = useProducts(companyId, { pageSize: 200 });
  const warehousesQuery = useWarehouses(companyId);
  const createMovement = useCreateMovement(companyId);

  const [kind, setKind] = useState<ManualMovementKind>("purchase");
  const [productSearch, setProductSearch] = useState("");
  const [productId, setProductId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [qty, setQty] = useState<string>("");
  const [unitCost, setUnitCost] = useState<string>("");
  const [reasonCode, setReasonCode] = useState("");
  const [notes, setNotes] = useState("");

  // Default warehouse on first load
  useEffect(() => {
    if (warehouseId) return;
    const def =
      warehousesQuery.data?.warehouses.find((w) => w.isDefault && w.isActive) ??
      warehousesQuery.data?.warehouses.find((w) => w.isActive);
    if (def) setWarehouseId(def.id);
  }, [warehousesQuery.data, warehouseId]);

  const stockableProducts = useMemo(
    () =>
      (productsQuery.data?.products ?? []).filter(
        (p) => p.kind === "product" && p.status === "active",
      ),
    [productsQuery.data],
  );
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return stockableProducts.slice(0, 50);
    const needle = productSearch.trim().toLowerCase();
    return stockableProducts
      .filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          p.sku?.toLowerCase().includes(needle),
      )
      .slice(0, 50);
  }, [stockableProducts, productSearch]);

  const selectedProduct = stockableProducts.find((p) => p.id === productId);
  const meta = KIND_META[kind];
  const isIn = meta.direction === "in";

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const qtyNum = Number(qty);
    if (!productId || !warehouseId || !qtyNum || qtyNum <= 0) {
      toast.error("Completa producto, bodega y cantidad mayor a 0.");
      return;
    }
    const unitCostNum = unitCost ? Number(unitCost) : undefined;
    if (isIn && (unitCostNum === undefined || unitCostNum < 0)) {
      toast.error("Las entradas requieren un costo unitario válido.");
      return;
    }
    createMovement.mutate(
      {
        productId,
        warehouseId,
        kind,
        qty: qtyNum,
        unitCost: unitCostNum,
        reasonCode: reasonCode || undefined,
        notes: notes || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Movimiento registrado");
          void navigate({
            to: "/companies/$companyId/inventario/movimientos",
            params: { companyId },
          });
        },
        onError: (err) =>
          toast.error("No pudimos registrar el movimiento", {
            description: err.message,
          }),
      },
    );
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <header className="space-y-1.5">
        <Link
          to="/companies/$companyId/inventario/movimientos"
          params={{ companyId }}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          ← Volver al kardex
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">
          Nuevo movimiento
        </h1>
        <p className="text-sm text-muted-foreground">
          Las entradas requieren costo unitario; las salidas descuentan al costo
          promedio actual y se rechazan si dejarían el saldo negativo (a menos
          que la empresa tenga el flag activado).
        </p>
      </header>

      <form className="flex flex-col gap-6" onSubmit={submit}>
        {/* ── Step 1: Movement kind picker as visual cards ─────────── */}
        <Card>
          <CardContent className="flex flex-col gap-3 py-4">
            <div>
              <Label>Tipo de movimiento</Label>
              <p className="text-xs text-muted-foreground">
                Elige el tipo que mejor describe esta operación.
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(Object.entries(KIND_META) as [ManualMovementKind, KindMeta][]).map(
                ([k, m]) => {
                  const Icon = m.icon;
                  const active = kind === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setKind(k)}
                      className={`flex items-start gap-2 rounded-md border p-3 text-left text-sm transition ${
                        active
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border hover:border-primary/50 hover:bg-accent"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 shrink-0 ${
                          m.direction === "in"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-rose-600 dark:text-rose-400"
                        }`}
                      />
                      <div className="min-w-0">
                        <div className="font-medium">{m.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {m.description}
                        </div>
                      </div>
                    </button>
                  );
                },
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Step 2: Product + warehouse + qty ─────────────────────── */}
        <Card>
          <CardContent className="grid gap-4 py-4">
            <div className="grid gap-3 lg:grid-cols-2">
              <div>
                <Label>Producto</Label>
                <div className="relative mt-1">
                  <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Buscar por nombre o SKU..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                  />
                </div>
                <select
                  className="mt-2 w-full rounded border bg-background px-2 py-2 text-sm"
                  size={6}
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                >
                  {filteredProducts.length === 0 && (
                    <option disabled value="">
                      Sin resultados
                    </option>
                  )}
                  {filteredProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.sku ? ` · ${p.sku}` : ""}
                      {" — "}
                      {fmtCop(p.unitPrice)}
                    </option>
                  ))}
                </select>
                {selectedProduct && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="muted">{selectedProduct.kind}</Badge>
                    <span>Precio venta · {fmtCop(selectedProduct.unitPrice)}</span>
                  </div>
                )}
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Solo se listan productos con tipo &quot;product&quot;. Activa
                  <code className="mx-0.5 rounded bg-muted px-1">is_stockable</code>
                  en la ficha del producto si no aparece.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <div>
                  <Label>Bodega</Label>
                  <select
                    className="mt-1 w-full rounded border bg-background px-2 py-2 text-sm"
                    value={warehouseId}
                    onChange={(e) => setWarehouseId(e.target.value)}
                  >
                    <option value="">— Selecciona —</option>
                    {(warehousesQuery.data?.warehouses ?? [])
                      .filter((w) => w.isActive)
                      .map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.code} — {w.name}
                          {w.isDefault ? " (predeterminada)" : ""}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.000001"
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      placeholder="10"
                    />
                  </div>
                  {isIn && (
                    <div>
                      <Label>Costo unitario (COP)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={unitCost}
                        onChange={(e) => setUnitCost(e.target.value)}
                        placeholder="1000"
                      />
                    </div>
                  )}
                </div>

                {!isIn && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      Las salidas se valoran al{" "}
                      <strong>costo promedio actual</strong> de la bodega. El
                      costo unitario no se especifica para este tipo.
                    </span>
                  </div>
                )}

                {isIn && qty && unitCost && (
                  <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 text-sm">
                    <span className="text-muted-foreground">Valor total</span>
                    <span className="font-semibold tabular-nums">
                      {fmtCop(Number(qty) * Number(unitCost))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Step 3: Optional metadata ─────────────────────────────── */}
        <Card>
          <CardContent className="grid gap-3 py-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Razón / código (opcional)</Label>
                <Input
                  value={reasonCode}
                  onChange={(e) => setReasonCode(e.target.value)}
                  placeholder="merma, daño, conteo, recuperación…"
                  maxLength={40}
                />
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Detalle visible en el kardex y el asiento contable"
                  maxLength={1000}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-end gap-2">
          <Button asChild variant="ghost">
            <Link
              to="/companies/$companyId/inventario/movimientos"
              params={{ companyId }}
            >
              Cancelar
            </Link>
          </Button>
          <Button type="submit" disabled={createMovement.isPending}>
            {createMovement.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            <ArrowUp className={isIn ? "hidden" : "h-4 w-4"} />
            <ArrowDown className={isIn ? "h-4 w-4" : "hidden"} />
            Registrar {meta.label.toLowerCase()}
          </Button>
        </div>
      </form>
    </div>
  );
}
